// ---------- shared state ----------
// The POC left these variables in global scope, implicitly shared between classic
// scripts. An explicit object costs an `S.` prefix and in exchange makes dependencies
// readable — and checkable by the compiler.

/** Zoom bounds. Zoom costs nothing in memory (the canvas keeps the screen size) and
 *  rendering only walks visible cells, hence a generous ceiling: at 24x, a 3px cell in a
 *  120x120 grid becomes 72px, large enough to read the codes even on a small phone. */
/**
 * Le zoom qui fait tenir la feuille entière dans la boîte, marge de papier comprise : c'est
 * l'échelle à laquelle render() dimensionne la case, donc 1 par construction (cf. le calcul de
 * sDev). C'est là que flyToWhole() ramène, et c'est la vue « d'ensemble » du jeu.
 */
export const ZOOM_FIT = 1;

/**
 * Et jusqu'où le joueur peut reculer AU-DELÀ de ça. En dessous de l'ajustement, la feuille
 * cesse de toucher les bords et flotte au milieu de la scène — clampView() la recentre dès
 * qu'elle est plus petite que la boîte. Ça ne sert pas à jouer (une case y fait moins d'un
 * pixel) mais à regarder : prendre du recul sur un grand dessin, et atteindre les plaques de
 * zone sans que la feuille déborde de l'écran.
 */
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 24;

export interface Viewport {
  z: number;
  x: number;
  y: number;
}

export const S = {
  // canvas (filled in at startup)
  sheet: null as unknown as HTMLCanvasElement,
  g: null as unknown as CanvasRenderingContext2D,
  DPR: 1,
  // Space actually available for the sheet, measured on the flex box. Two dimensions and
  // not just one: a landscape image and a portrait one don't waste the same side, and the
  // sheet must fill whatever it's given.
  availW: 600,
  availH: 600,
  /** true during a pinch or a drag: rendering transforms the existing layers instead of
   *  rasterizing them again on every frame */
  gesture: false,

  // grid
  alphabetId: 'motifs',
  gridW: 48,
  gridH: 48, // actual dimensions (image aspect ratio)
  cellW: 1,
  cellH: 1, // size of one cell (not square in ASCII)
  pxW: 1,
  pxH: 1, // canvas dimensions, in logical px

  // play grid
  target: [] as number[][], // target symbol per cell, 0..9
  filled: [] as boolean[][],
  /** Cell index (r * gridW + c) of every successful fill, in order. This IS the saved progress:
   *  one entry per filled cell, so replaying the list rebuilds `filled` by simple assignment. */
  taps: [] as number[],
  /** Catalogue puzzle currently loaded, and the fingerprint of its grid. null for the demo
   *  image, which is never saved — it isn't a puzzle anyone chose. */
  puzzleId: null as string | null,
  puzzleFp: '',

  // viewport and game
  viewport: { z: 1, x: 0, y: 0 } as Viewport,
  lockedSymbol: null as number | null, // null = auto ; otherwise the imposed symbol
  /** Cell under the mouse cursor, i.e. the single cell a click would fill. */
  hover: null as [number, number] | null,
  hoverKey: '',
  mistake: null as { r: number; c: number; t0: number } | null,
  // Player preferences, restored at startup and written back on change by src/settings.ts.
  sound: false,
  haptics: true,
  score: 0,
  combo: 0,
  comboAt: 0,
  gameOver: false,
  /** true entre le moment où finishGame() vient de conclure CETTE partie et le moment où le
   *  joueur quitte la feuille — jamais quand `gameOver` vaut vrai parce qu'un puzzle déjà fini
   *  a simplement été rouvert (home.ts:restore ne le touche pas). C'est la distinction dont a
   *  besoin l'interstitielle de fin de puzzle (cf. ads.ts, home.ts:openHome) : une pub à la
   *  sortie d'une partie qu'on vient de finir, jamais à la sortie d'une consultée à nouveau. */
  justFinished: false,
  /** Étoiles posées sur CE puzzle (0 = aucune sélection, 1..5 = étoiles pleines). Remis à 0 dans
   *  resetPlay(), reposé depuis la sauvegarde par home.ts:restore() en rouvrant un puzzle fini. */
  rating: 0,
};

/**
 * Measures the offered box: it's also the canvas size, since it fills it entirely. Its
 * height comes from the flex layout and not from its content, so there's no circularity
 * with the drawing's size.
 */
export function measureScene(): void {
  const box = document.querySelector<HTMLElement>('.sheetbox');
  if (!box) return;
  S.availW = Math.max(160, Math.floor(box.clientWidth));
  S.availH = Math.max(160, Math.floor(box.clientHeight));
}
